"use client";

import type { ProductDetails } from "@ellaandil/formatters";
import { parseAsString, useQueryState } from "nuqs";
import { useEffect, useMemo } from "react";
import { useDPC } from "@/lib/depict/dpc";
import { getDepictVariantId } from "@/lib/depict/product-id";

type Items = ProductDetails["items"];
type Item = NonNullable<Items>[number];

const inStock = (stock: Item["stock"]) => stock !== "no" && stock !== 0 && stock != null;

/**
 * Fires Depict's product page view tracking. DPC ties the next click/event to
 * this product context, which Depict uses to attribute downstream revenue and
 * power recommendations.
 *
 * Depict's `product_id` is variant-level for ellaandil (e.g. `EITE06103xs`).
 * `?size=` would be the ideal source (and re-fire on a switch), but nothing
 * currently writes it to the URL — `SizeSelector` only keeps the selection in
 * local state — so it's `null` on a normal page load. Fall back to the same
 * "first in-stock size" `SizeSelector` auto-selects by default (mirrors
 * `ProductRecommendations`'s derivation) so the view still gets attributed
 * instead of silently never firing.
 *
 * See https://docs.depict.ai/api-guide/tracking/product-pages
 */
export const DepictView = ({ items }: { items: Items }) => {
	const { data: dpc } = useDPC();
	const [urlSize] = useQueryState("size", parseAsString);
	const firstAvailableSize = useMemo(
		() => items?.find((item) => inStock(item.stock))?.name ?? items?.[0]?.name ?? null,
		[items],
	);
	const productId = getDepictVariantId(items, urlSize ?? firstAvailableSize);

	useEffect(() => {
		if (!dpc || !productId) return;
		dpc.setProductId(productId);
	}, [dpc, productId]);

	return null;
};
